export function resolveVariant(variants, options) {
  return variants.find((variant) => variant.options.length === options.length && variant.options.every((value, index) => value === options[index])) || null;
}
export function initialVariant(variants, selectedId) {
  return variants.find((variant) => String(variant.id) === String(selectedId)) || (variants.length === 1 ? variants[0] : null);
}
export function explicitOptionIndexes(variants, selectedId) {
  const selected = variants.find((variant) => String(variant.id) === String(selectedId));
  return new Set(selected ? selected.options.map((_, index) => index) : []);
}
export function galleryIndex(index, count) { return count ? ((index % count) + count) % count : 0; }
export function purchaseState(variant, pending = false) {
  return { disabled: pending || !variant?.available, label: pending ? 'Adding...' : !variant ? 'Select an Option' : variant.available ? 'Add to Bag' : 'Out Of Stock' };
}
export async function addToBag(fetcher, url, body) {
  const response = await fetcher(url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body });
  const result = await response.json();
  if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
  return result;
}
if (typeof customElements !== 'undefined' && !customElements.get('se-product')) {
  customElements.define('se-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('form');
      this.input = this.form.querySelector('input[name=id]');
      this.input.disabled = false;
      this.variant = initialVariant(this.variants, this.dataset.selectedVariant);
      this.explicitOptions = explicitOptionIndexes(this.variants, this.dataset.selectedVariant);
      this.options = this.variant ? [...this.variant.options] : Array(this.variants[0]?.options.length || 0).fill(null);
      this.input.value = this.variant?.id || '';
      this.submit = this.form.querySelector('[type=submit]');
      this.error = this.form.querySelector('[role=status]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.current = 0;
      this.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-thumbnail]');
        const direction = event.target.closest('[data-direction]');
        if (thumbnail) this.showImage(Number(thumbnail.dataset.thumbnail));
        if (direction) this.showImage(this.current + Number(direction.dataset.direction));
        const option = event.target.closest('[data-option-value]');
        if (!option || this.pending) return;
        const index = Number(option.closest('[data-option-index]').dataset.optionIndex);
        this.options[index] = option.dataset.optionValue;
        this.explicitOptions.add(index);
        this.variant = resolveVariant(this.variants, this.options);
        this.input.value = this.variant?.id || '';
        this.error.hidden = true;
        this.update();
        const url = new URL(window.location.href);
        if (this.variant) url.searchParams.set('variant', this.variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
        const imageId = this.variant?.featured_image?.id;
        if (imageId) {
          const imageIndex = this.slides.findIndex((slide) => slide.dataset.imageId === String(imageId));
          if (imageIndex >= 0) this.showImage(imageIndex);
        }
      });
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (purchaseState(this.variant, this.pending).disabled) return;
        this.pending = true;
        this.error.hidden = true;
        this.update();
        const drawer = document.querySelector('cart-drawer');
        try {
          const body = new FormData(this.form);
          if (drawer) {
            body.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
            body.append('sections_url', window.location.pathname);
            drawer.setActiveElement(this.submit);
          }
          const result = await addToBag(window.fetch.bind(window), window.routes?.cart_add_url || '/cart/add.js', body);
          if (drawer) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(result);
          } else window.location.assign(window.routes?.cart_url || '/cart');
        } catch (error) {
          this.error.textContent = error.message || 'Unable to add this item. Please try again.';
          this.error.hidden = false;
        } finally { this.pending = false; this.update(); }
      });
      this.update();
    }
    update() {
      const state = purchaseState(this.variant, this.pending);
      if (!this.pending && !this.variants.some((variant) => variant.available)) state.label = 'Out Of Stock';
      this.submit.disabled = state.disabled;
      this.submit.textContent = state.label;
      this.submit.setAttribute('aria-busy', String(Boolean(this.pending)));
      for (const group of this.querySelectorAll('[data-option-index]')) {
        const index = Number(group.dataset.optionIndex);
        for (const button of group.querySelectorAll('[data-option-value]')) {
          const value = button.dataset.optionValue;
          button.setAttribute('aria-pressed', String(this.explicitOptions.has(index) && this.options[index] === value));
          const candidate = [...this.options]; candidate[index] = value;
          button.dataset.unavailable = String(!this.variants.some((variant) => variant.available && candidate.every((choice, position) => !choice || variant.options[position] === choice)));
          // Keep options reachable when moving between combinations with multiple changed dimensions.
          button.disabled = Boolean(this.pending);
        }
      }
      if (this.variant) this.querySelector('[data-price]').textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency, maximumFractionDigits: 0 }).format(this.variant.price / 100);
    }
    showImage(index) {
      this.current = galleryIndex(index, this.slides.length);
      this.slides.forEach((slide, position) => { slide.hidden = position !== this.current; });
      this.querySelectorAll('[data-thumbnail]').forEach((button) => button.setAttribute('aria-current', String(Number(button.dataset.thumbnail) === this.current)));
    }
  });
}
